// The visit status rules behind the Mark as Complete and Record Clock-Out dialogs.
//
// - canMarkAsComplete: the dashboard tabs show their Mark as Complete buttons by it. The row
//   menus of the schedule pages and the details page still gate their items on the status inline.
// - completionNeedsReason, keepsRealClockIn, reportTimes: the Mark as Complete dialog.
// - reportTimes: also the end time of the Record Clock-Out dialog.
// - canDeleteVisit: not used by any page. The page tests use it as the oracle for which rows
//   offer Delete Visit.
import {utcIsoToZonedWallTime} from './helpers'

// Visits that are not clocked out yet: the office can still complete them, but only as a
// manual EVV entry.
const NEVER_CLOCKED_OUT = ['scheduled', 'in_progress']
const CLOCKED_OUT = ['ended', 'finished']

export const canMarkAsComplete = status => NEVER_CLOCKED_OUT.includes(status) || CLOCKED_OUT.includes(status)

// The caregiver's clock-in of a visit somebody really clocked in to. A scheduled visit has none:
// cancel and reschedule keep a worked visit's clock data, so whatever its clock columns hold is a
// leftover, and the backend clears it when the visit is completed.
export const recordedClockIn = visit => (visit?.status === 'scheduled' ? null : visit?.clock_in_time || null)

// The caregiver's clock-out, once the visit is ended or finished. A scheduled or in-progress
// visit has none: a clock-out on it is a leftover, and the backend replaces it whole.
export const recordedClockOut = visit => (CLOCKED_OUT.includes(visit?.status) ? visit?.clock_out_time || null : null)

// The reason follows the clock-out, not the status alone: completing a visit with no recorded
// clock-out is a manual EVV entry and needs one. That is every scheduled or in-progress visit
// (the backend's ManualEntry), plus the rare ended/finished visit that never got a clock-out.
export const completionNeedsReason = visit => canMarkAsComplete(visit?.status) && !recordedClockOut(visit)

// An in-progress visit keeps the caregiver's real clock-in (and GPS) when it is completed;
// only the end time is applied, as a manual clock-out.
export const keepsRealClockIn = visit => visit?.status === 'in_progress' && Boolean(visit?.clock_in_time)

// The start and end the report-time fields begin with. The caregiver's real clock times win
// (EVV integrity: completing without editing must not replace them with the scheduled times),
// read as wall-clock "HH:mm" in the schedule's zone. A time nobody recorded falls back to the
// scheduled one.
export const reportTimes = visit => {
    const zone = visit?.schedule?.timezone
    const clockIn = recordedClockIn(visit)
    const clockOut = recordedClockOut(visit)
    return {
        start_time: clockIn ? utcIsoToZonedWallTime(clockIn, zone) : visit?.start_time,
        end_time: clockOut ? utcIsoToZonedWallTime(clockOut, zone) : visit?.end_time,
    }
}

// Delete Visit removes one occurrence, and only one that has not started.
export const canDeleteVisit = status => status === 'scheduled'
